import { ChevronDownIcon, CopyIcon } from '@radix-ui/react-icons';
import { useCallback, useRef } from 'react';

import { Button } from '../../../primitives/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '../../../primitives/drawer';
import { ScrollArea, ScrollBar } from '../../../primitives/scroll-area';
import { ButtonClipboard } from '../../button/button-clipboard';
import {
  InputBucketIn as InputBucketInUI,
  type InputBucketInProps,
  type InputBucketInRef,
} from '../../input/input-bucket-in';
import { bucketsToRanges } from '../../input/input-bucket-in/utils/buckets-to-ranges';

export function InputBucketIn({
  disabled = false,
  readonly = false,
  saltBaseOptions,
  value = { ranges: [], lockedQuadrants: [], saltBase: '' },
  onChange,
  onSaltBaseChange,
}: InputBucketInProps) {
  const inputRef = useRef<InputBucketInRef>(null);
  const handleDrawerOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen || disabled) return;

      const hasRanges = Array.isArray(value?.ranges) && value.ranges.length > 0;
      if (hasRanges) return;

      onChange({
        ...value,
        type: 'rollout',
        lockedQuadrants: [],
        ranges: bucketsToRanges(Array.from({ length: 50 }, (_, bucketIndex) => bucketIndex)),
      });
    },
    [disabled, onChange, value],
  );

  const selectedBucketCount = value.ranges.reduce(
    (total, { from, to }) => total + Math.max(0, to - from + 1),
    0,
  );

  const seed =
    value?.saltBase && value?.salt
      ? `${value.saltBase} + ${value.salt}`
      : value?.saltBase
        ? value.saltBase
        : value?.salt
          ? value.salt
          : undefined;

  const triggerButton = (
    <Button
      title="Configurar un segmento"
      className="h-auto min-h-[4.5rem] flex-auto justify-between gap-4 rounded-lg px-3 py-2 select-none"
      disabled={disabled}
      style={{
        backgroundImage: `linear-gradient(to right, hsl(var(--success) / 0.5) ${selectedBucketCount}%, transparent ${selectedBucketCount}%)`,
      }}
      variant="outline"
    >
      <p className="grid space-y-1 text-left">
        <span className="truncate text-sm">
          {selectedBucketCount === 0 ? (
            'Seleccionar'
          ) : value?.type === 'rollout' ? (
            <>
              Rollout del{' '}
              <span className="rounded-lg bg-accent px-2 text-accent-foreground">
                {selectedBucketCount} %
              </span>
            </>
          ) : (
            <>
              El usuario está en cualquiera de los{' '}
              <span className="rounded-lg bg-accent px-2 text-accent-foreground">
                {selectedBucketCount} buckets
              </span>{' '}
              seleccionados
            </>
          )}
        </span>
        <span className="truncate text-xs">Semilla: {seed || 'seleccionar'}</span>
      </p>
      <ChevronDownIcon className="size-4 opacity-50" />
    </Button>
  );

  return (
    <div className="flex items-center gap-4">
      <ButtonClipboard
        className="flex-none"
        disabled={!seed}
        label={<CopyIcon className="size-4" />}
        size="icon"
        title="Copiar semilla"
        valueToCopy={`semilla: ${seed}`}
      />
      <Drawer handleOnly onOpenChange={handleDrawerOpenChange}>
        {!disabled ? <DrawerTrigger asChild>{triggerButton}</DrawerTrigger> : triggerButton}

        <DrawerContent onAnimationEnd={() => inputRef.current?.cancelRange()}>
          <DrawerHeader className="p-0">
            <DrawerTitle className="sr-only">Configurar un segmento</DrawerTitle>
            <DrawerDescription className="sr-only">Configurar un segmento</DrawerDescription>
          </DrawerHeader>
          <ScrollArea className="grid size-full">
            <div className="m-auto w-full pb-4 max-md:px-10 md:w-[650px]">
              <InputBucketInUI
                disabled={disabled}
                readonly={readonly}
                ref={inputRef}
                saltBaseOptions={saltBaseOptions}
                value={value}
                onChange={onChange}
                onSaltBaseChange={onSaltBaseChange}
              />
            </div>
            <ScrollBar orientation="vertical" />
          </ScrollArea>

          <DrawerFooter className="border-t px-0">
            <div className="m-auto w-full max-md:px-10 md:w-[650px]">
              <DrawerClose asChild>
                <Button className="w-full">Continuar</Button>
              </DrawerClose>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
